import { GroupDef, GroupMarkup, PriceSpec } from "../../pricingData";
import PriceSlider from "../shared/PriceSlider";
import BucketTooltip from "../shared/BucketTooltip";
import { eur } from "../shared/formatters";
import styles from "../../PricingTool.module.css";

export interface PriceFloor {
  cost: PriceSpec;
  withMarkup: number;
  hasMarkup: boolean;
}

interface Props {
  def: GroupDef;
  buckets: string[];
  price: PriceSpec;
  volShare: number;
  txnShare: number;
  groupVolume: number;
  // This group's transactions a month. Both units are on the row, because a
  // percentage price earns on the volume and a fixed one earns per
  // transaction, so neither figure answers the row on its own.
  groupTxns: number;
  // Lead with the unit the shares are typed in; the other becomes the caption.
  byCount: boolean;
  editable: boolean;
  manualPct?: number;
  // What the typed weight is a share of. One basis for every row, unlike the
  // read-only display, which shows each group on the basis its own price earns
  // on — a mix is only typeable if every box means the same thing.
  manualBasis?: string;
  onManualPct?: (v: number) => void;
  onSetVal: (v: number) => void;
  onSetType: (t: "Percentage" | "Fixed") => void;
  // The partners' cuts of this group's price, null when nothing was measured.
  markup: GroupMarkup | null;
  // Klearly's share: the price less both cuts. Derived upstream so the four
  // figures on screen cannot disagree about what the merchant is charged.
  take: PriceSpec;
  floor: PriceFloor;
  open: boolean;
  onToggle: () => void;
  onSetMarkup: (part: "reseller" | "isv", v: number) => void;
  // Manual has no measured contract, so the closed row offers to add one
  // instead of stating a cut that was never there.
  manualEntry?: boolean;
}

/** A rate in the unit its group is priced in. */
function rateText(spec: PriceSpec): string {
  return spec.valueType === "Fixed"
    ? `€${spec.value.toFixed(3)}`
    : `${spec.value.toFixed(3)}%`;
}

export default function PriceRow({
  def,
  buckets,
  price,
  volShare,
  txnShare,
  groupVolume,
  groupTxns,
  byCount,
  editable,
  manualPct,
  manualBasis,
  onManualPct,
  onSetVal,
  onSetType,
  markup,
  take,
  floor,
  open,
  onToggle,
  onSetMarkup,
  manualEntry,
}: Props) {
  const isPct = price.valueType === "Percentage";
  const sMax = isPct ? 4 : 0.3;
  const sStep = isPct ? 0.01 : 0.005;
  const shareVal = isPct ? volShare : txnShare;
  // Typed weights are held to one decimal so a measured mix can be shown as it
  // was measured; rounding to whole numbers made every row look edited.
  const round1 = (n: number) => Math.round(n * 10) / 10;
  const txnsText = (n: number) => Math.round(n).toLocaleString("en-US");
  const basisLabel = isPct ? "of volume" : "of txns";

  // A price can clear processing cost and still earn Klearly nothing, because
  // the partners are paid out of the same fee first. Both floors are worth
  // naming, and they are different warnings: under cost loses money outright,
  // under cost-plus-markup earns the partners and nobody else.
  const sameUnit = price.valueType === floor.cost.valueType;
  const belowCost = sameUnit && price.value < floor.cost.value;
  const belowFloor = sameUnit && !belowCost && price.value < floor.withMarkup;

  // Any measured cohort can be broken down, including one that pays no partner
  // today: the panel is where a cut is typed, so gating it on a cut already
  // existing is what stops anyone asking what adding a reseller would cost.
  // Zero is stated as zero rather than hidden — that is a fact about the
  // cohort, and a reader who cannot see it cannot tell it from missing data.
  const hasParts = !!markup;
  const paysPartner =
    !!markup && (markup.reseller.value > 0 || markup.isv.value > 0);

  const partRow = (
    label: string,
    spec: PriceSpec,
    onChange: ((v: number) => void) | null,
    note: string
  ) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontSize: 11.5,
        padding: "4px 0",
      }}
    >
      <span style={{ width: 118, flexShrink: 0, color: "rgba(0,0,0,0.6)" }}>
        {label}
      </span>
      {onChange ? (
        <div className={styles.price_input_wrap} style={{ width: 92 }}>
          {spec.valueType === "Fixed" && (
            <span className={styles.price_prefix}>€</span>
          )}
          <input
            type="number"
            className={styles.price_number_input}
            min={0}
            step={spec.valueType === "Fixed" ? 0.001 : 0.01}
            value={Number(spec.value.toFixed(4))}
            onChange={(e) => {
              const n = parseFloat(e.target.value);
              onChange(Number.isNaN(n) ? 0 : Math.max(0, n));
            }}
          />
          {spec.valueType === "Percentage" && (
            <span className={styles.price_suffix}>%</span>
          )}
        </div>
      ) : (
        <span
          style={{
            width: 92,
            flexShrink: 0,
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
            color: spec.value > 0 ? "#6B4EFF" : "#ff4d4f",
          }}
        >
          {rateText(spec)}
        </span>
      )}
      <span style={{ fontSize: 10.5, color: "rgba(0,0,0,0.4)" }}>{note}</span>
    </div>
  );

  return (
    <div className={styles.group_row}>
      <BucketTooltip buckets={buckets}>
        <div style={{ cursor: "default" }}>
          <div className={styles.group_label}>{def.label}</div>
          <div className={styles.group_buckets}>{def.buckets}</div>
        </div>
      </BucketTooltip>

      <div>
        {editable && onManualPct != null ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div className={styles.price_input_wrap}>
              <input
                type="number"
                className={styles.share_number_input}
                min={0}
                max={100}
                step={1}
                value={round1(manualPct ?? shareVal)}
                onChange={(e) => {
                  const n = parseFloat(e.target.value);
                  onManualPct?.(
                    Number.isNaN(n) ? 0 : Math.max(0, Math.min(100, n))
                  );
                }}
              />
              <span className={styles.price_suffix}>%</span>
            </div>
            <span className={styles.share_basis}>
              {manualBasis ?? basisLabel}
            </span>
          </div>
        ) : (
          <div>
            <div className={styles.share_value}>{shareVal.toFixed(1)}%</div>
            <div className={styles.share_basis}>{basisLabel}</div>
          </div>
        )}
      </div>

      <div>
        <div className={styles.share_value}>
          {byCount ? txnsText(groupTxns) : eur(groupVolume)}
        </div>
        <div className={styles.share_basis}>
          {byCount ? eur(groupVolume) : `${txnsText(groupTxns)} txns`} / month
        </div>
      </div>

      <div className={styles.price_control}>
        <div className={styles.price_slider_wrap}>
          <PriceSlider
            value={price.value}
            max={sMax}
            step={sStep}
            cost={floor.hasMarkup ? floor.withMarkup : floor.cost.value}
            costMax={sMax}
            onChange={onSetVal}
          />
          {belowCost && (
            <span className={styles.below_cost_warn}>
              ⚠ below processing cost
            </span>
          )}
          {belowFloor && (
            <span className={styles.below_cost_warn}>
              ⚠ above cost but below cost + partner markup — the partners earn,
              Klearly does not
            </span>
          )}
          {hasParts && (
            <button
              type="button"
              className={styles.parts_toggle}
              onClick={onToggle}
              aria-expanded={open}
            >
              {/* Always the take, including where it equals the whole fee:
                  that it does is the useful part, and a row that quotes a
                  figure on some cohorts and a phrase on others is harder to
                  scan than one that always quotes the same thing. */}
              {manualEntry && !paysPartner
                ? open
                  ? "Hide partner markup"
                  : "Add partner markup"
                : `${open ? "Hide" : "Show"} price components · Klearly keeps ${rateText(take)}${paysPartner ? "" : " — no partner cut"}`}
            </button>
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            flexShrink: 0,
          }}
        >
          <div className={styles.price_input_wrap}>
            {!isPct && <span className={styles.price_prefix}>€</span>}
            <input
              type="number"
              className={styles.price_number_input}
              step={sStep}
              value={price.value}
              onChange={(e) =>
                onSetVal(
                  isNaN(parseFloat(e.target.value))
                    ? 0
                    : parseFloat(e.target.value)
                )
              }
            />
            {isPct && <span className={styles.price_suffix}>%</span>}
          </div>
          <div className={styles.type_toggle}>
            <button
              className={`${styles.type_btn} ${!isPct ? styles.type_btn_active : styles.type_btn_inactive}`}
              onClick={() => onSetType("Fixed")}
            >
              €
            </button>
            <button
              className={`${styles.type_btn} ${isPct ? styles.type_btn_active : styles.type_btn_inactive}`}
              onClick={() => onSetType("Percentage")}
            >
              %
            </button>
          </div>
        </div>
      </div>

      {/* Spans the row's own grid, so the four columns above keep their widths. */}
      {open && hasParts && markup && (
        <div className={styles.parts_panel} style={{ gridColumn: "1 / -1" }}>
          {partRow(
            "Merchant pays",
            price,
            onSetVal,
            "the total fee on the proposal"
          )}
          {partRow(
            "Reseller markup",
            markup.reseller,
            (v) => onSetMarkup("reseller", v),
            "the reseller's cut of that fee"
          )}
          {partRow(
            "ISV markup",
            markup.isv,
            (v) => onSetMarkup("isv", v),
            "the software vendor's cut"
          )}
          <div
            style={{
              borderTop: "1px solid rgba(0,0,0,0.08)",
              marginTop: 4,
              paddingTop: 4,
            }}
          >
            {partRow(
              "Klearly keeps",
              take,
              null,
              !paysPartner
                ? `the whole fee, before ${rateText(floor.cost)} of processing cost`
                : take.value > 0
                  ? `before ${rateText(floor.cost)} of processing cost`
                  : "the partners take the whole fee"
            )}
          </div>
        </div>
      )}
    </div>
  );
}
